/**
 * Phép tính thuần của Trung tâm thông báo (ADR-0102): dựng giá trị form từ `NotifyField`, kiểm và đóng gói `config`
 * trước khi gửi, gom kênh theo loại, luật gửi lại. Không dựng chuỗi hiển thị: trả mã để view tự dịch.
 */
import type {
  GiaTriCauHinh,
  MaKenh,
  NotifyAlarmTask,
  NotifyChannel,
  NotifyDelivery,
  NotifyField,
  NotifyUsage,
} from '../types.ts';

/** Thứ tự loại kênh trên màn: Telegram và Email trước (ADR-0102 mục 3). */
export const THU_TU_KENH: readonly MaKenh[] = [
  'TELEGRAM',
  'EMAIL',
  'WEBHOOK',
  'DISCORD',
  'DINGTALK',
  'WECOM',
  'FEISHU',
];

/** Giá trị `If-Match` từ `version`: ETag của notification-service là phiên bản trong ngoặc kép. */
export function etagTuPhienBan(version: number): string {
  return `"${version}"`;
}

/** Mã loại cảnh báo có dấu chấm (`subscription.expiring`); khoá dịch không được chứa dấu chấm. */
export function khoaLoai(code: string): string {
  return code.replace(/\./g, '_');
}

/** Giá trị form của một ô: chuỗi cho ô chữ, số cho NUMBER, boolean cho BOOLEAN. */
export type GiaTriO = string | number | boolean | null;
export type GiaTriForm = Record<string, GiaTriO>;

/** Giá trị form ban đầu: từ cấu hình đang có (khi sửa), không thì từ `default_value`. Ô secret luôn trống. */
export function giaTriBanDau(fields: readonly NotifyField[], dangCo?: GiaTriCauHinh): GiaTriForm {
  const ra: GiaTriForm = {};
  for (const f of fields) {
    if (f.secret) {
      ra[f.key] = '';
      continue;
    }
    const co = dangCo?.[f.key];
    const nguon = co !== undefined ? co : f.default_value;
    if (f.kind === 'BOOLEAN') ra[f.key] = nguon === true || nguon === 'true';
    else if (f.kind === 'NUMBER')
      ra[f.key] = typeof nguon === 'number' ? nguon : nguon == null || nguon === '' ? null : Number(nguon);
    else if (f.kind === 'EMAIL_LIST' && Array.isArray(nguon)) ra[f.key] = nguon.join('\n');
    else if (f.kind === 'JSON' && nguon !== null && typeof nguon === 'object')
      ra[f.key] = JSON.stringify(nguon, null, 2);
    else ra[f.key] = nguon == null ? '' : String(nguon);
  }
  return ra;
}

/** Tách ô EMAIL_LIST (mỗi dòng một địa chỉ, nhận cả dấu phẩy, chấm phẩy) thành danh sách. */
export function tachEmail(text: string): string[] {
  return text
    .split(/[\n,;]+/)
    .map((s) => s.trim())
    .filter(Boolean);
}

const EMAIL = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

export type LoiTruong = 'batBuoc' | 'emailSai' | 'soNgoaiKhoang' | 'jsonSai' | 'urlSai' | 'gioSai';

function rong(v: GiaTriO | undefined): boolean {
  return v === null || v === undefined || (typeof v === 'string' && v.trim() === '');
}

/**
 * Ô bị ẩn vì ô khác: EMAIL bật "dùng máy chủ thư của nền tảng" thì không cần SMTP riêng (ADR-0102 mục 3).
 */
export function oBiAn(kenh: MaKenh | null, key: string, giaTri: GiaTriForm): boolean {
  if (kenh !== 'EMAIL' || giaTri.use_platform_smtp !== true) return false;
  return key.startsWith('smtp_') || key === 'sender';
}

/**
 * Kiểm form. Ô secret khi sửa mà đã có giá trị (`secretsSet`) thì để trống là giữ, không coi là thiếu.
 */
export function kiemForm(
  fields: readonly NotifyField[],
  giaTri: GiaTriForm,
  opts: { kenh?: MaKenh | null; secretsSet?: readonly string[] } = {},
): Record<string, LoiTruong> {
  const loi: Record<string, LoiTruong> = {};
  for (const f of fields) {
    if (oBiAn(opts.kenh ?? null, f.key, giaTri)) continue;
    const v = giaTri[f.key];
    const daLuu = f.secret && (opts.secretsSet ?? []).includes(f.key);
    if (rong(v)) {
      if (f.required && f.kind !== 'BOOLEAN' && !daLuu) loi[f.key] = 'batBuoc';
      continue;
    }
    if (f.kind === 'NUMBER') {
      const so = Number(v);
      if (!Number.isFinite(so) || (f.min != null && so < f.min) || (f.max != null && so > f.max)) {
        loi[f.key] = 'soNgoaiKhoang';
      }
    } else if (f.kind === 'EMAIL_LIST') {
      if (tachEmail(String(v)).some((e) => !EMAIL.test(e))) loi[f.key] = 'emailSai';
    } else if (f.kind === 'JSON' && !f.secret) {
      try {
        JSON.parse(String(v));
      } catch {
        loi[f.key] = 'jsonSai';
      }
    } else if (f.kind === 'URL' || (f.secret && /url$/i.test(f.key))) {
      if (!/^https:\/\/\S+$/i.test(String(v).trim())) loi[f.key] = 'urlSai';
    } else if (f.kind === 'TIME') {
      if (!/^([01]\d|2[0-3]):[0-5]\d$/.test(String(v))) loi[f.key] = 'gioSai';
    }
  }
  return loi;
}

/**
 * `config` gửi lên: NUMBER thành số, EMAIL_LIST thành mảng, JSON giữ nguyên chuỗi đã kiểm. Ô secret để trống thì
 * **không gửi** — server giữ giá trị cũ (ADR-0102 mục 4). Ô bị ẩn không gửi.
 */
export function dongGoiCauHinh(
  fields: readonly NotifyField[],
  giaTri: GiaTriForm,
  kenh: MaKenh | null = null,
): GiaTriCauHinh {
  const ra: GiaTriCauHinh = {};
  for (const f of fields) {
    if (oBiAn(kenh, f.key, giaTri)) continue;
    const v = giaTri[f.key];
    if (f.kind === 'BOOLEAN') {
      ra[f.key] = v === true;
      continue;
    }
    if (rong(v)) continue;
    if (f.kind === 'NUMBER') ra[f.key] = Number(v);
    else if (f.kind === 'EMAIL_LIST') ra[f.key] = tachEmail(String(v));
    else ra[f.key] = typeof v === 'string' ? v.trim() : v;
  }
  return ra;
}

/** Số cấu hình của từng loại kênh. */
export function demTheoLoai(kenh: readonly NotifyChannel[]): Map<MaKenh, NotifyChannel[]> {
  const ra = new Map<MaKenh, NotifyChannel[]>();
  for (const k of kenh) {
    const ds = ra.get(k.channel_type) ?? [];
    ds.push(k);
    ra.set(k.channel_type, ds);
  }
  return ra;
}

/** Chỉ lần gửi đã dừng hẳn mới gửi lại được (ADR-0102 mục 8). */
export function guiLaiDuoc(d: Pick<NotifyDelivery, 'status'>): boolean {
  return d.status === 'FAILED_PERMANENT' || d.status === 'DEAD_LETTERED' || d.status === 'CANCELLED';
}

/** Cặp (tham số, giá trị) của điều kiện tác vụ, theo thứ tự khai trong loại cảnh báo; bỏ ô trống. */
export function dieuKienCuaTacVu(
  task: Pick<NotifyAlarmTask, 'params'>,
  params: readonly NotifyField[],
): Array<{ key: string; giaTri: string }> {
  return params
    .filter((p) => task.params[p.key] !== undefined && task.params[p.key] !== null && task.params[p.key] !== '')
    .map((p) => ({ key: p.key, giaTri: String(task.params[p.key]) }));
}

/** Phần trăm hạn mức tháng đã dùng; `null` khi không giới hạn. */
export function phanTramDaDung(u: NotifyUsage): number | null {
  if (u.limit == null || u.limit <= 0) return null;
  return Math.min(100, Math.round((u.sent / u.limit) * 100));
}

/** Khung giờ qua nửa đêm (`22:00`–`06:00`) là hợp lệ; chỉ kiểm định dạng. */
export function khungGioHopLe(tu: string, den: string): boolean {
  const g = /^([01]\d|2[0-3]):[0-5]\d$/;
  return g.test(tu) && g.test(den);
}

/** Số nguyên dương hoặc trống (không giới hạn); trả `undefined` khi sai. */
export function docGioiHan(text: string): number | null | undefined {
  const s = text.trim();
  if (s === '') return null;
  if (!/^\d+$/.test(s)) return undefined;
  const n = Number(s);
  return n >= 1 ? n : undefined;
}
